import { addDays, addMonths, addMonthsToPeriod, daysBetween, daysInMonth, isoWeekday, makeDate, monthsBetween, parts, periodOf, periodParts, } from './dates.js';
export function occurrenceKey(conceptId, originalDate) {
    return `${conceptId}:${originalDate}`;
}
export function sortVersions(versions) {
    return [...versions].sort((a, b) => (a.validFrom < b.validFrom ? -1 : a.validFrom > b.validFrom ? 1 : a.createdAt < b.createdAt ? -1 : 1));
}
/** Versión vigente en una fecha (la última con validFrom <= fecha). */
export function versionAt(versions, date) {
    let current = null;
    for (const v of sortVersions(versions)) {
        if (v.validFrom <= date)
            current = v;
        else
            break;
    }
    return current;
}
/** Segmentos de vigencia [validFrom, nextValidFrom) de cada versión. */
export function versionSegments(versions) {
    const sorted = sortVersions(versions);
    return sorted.map((version, i) => {
        const next = sorted[i + 1];
        return { version, from: version.validFrom, to: next ? addDays(next.validFrom, -1) : null };
    });
}
/** ¿Hay alguna versión activa cuyo segmento toque el período? */
export function isActiveInPeriod(versions, period) {
    const { year, month } = periodParts(period);
    const first = makeDate(year, month, 1);
    const last = makeDate(year, month, daysInMonth(year, month));
    return versionSegments(versions).some((s) => s.version.state === 'active' && s.from <= last && (s.to === null || s.to >= first));
}
function clampDay(year, month, day) {
    return makeDate(year, month, Math.min(day, daysInMonth(year, month)));
}
function shiftPeriod(date, offset) {
    return addMonthsToPeriod(periodOf(date), offset);
}
function kindFor(concept) {
    if (concept.behavior === 'income')
        return 'income';
    if (concept.behavior === 'loan')
        return 'repayment';
    return concept.direction === 'income' ? 'income' : 'expense';
}
/**
 * Genera las fechas programadas de una versión dentro de un segmento de vigencia y un rango.
 * Devuelve pares [fecha, número de cuota].
 */
function scheduledDates(version, segFrom, segTo, range) {
    const from = segFrom > range.from ? segFrom : range.from;
    const toCandidates = [range.to, segTo].filter((d) => d !== null);
    const to = toCandidates.reduce((a, b) => (a < b ? a : b));
    if (from > to)
        return [];
    const s = version.schedule;
    const out = [];
    switch (version.frequency) {
        case 'weekly': {
            const weekday = s.weekday ?? isoWeekday(s.referenceDate ?? version.validFrom);
            let d = from;
            while (isoWeekday(d) !== weekday)
                d = addDays(d, 1);
            for (; d <= to; d = addDays(d, 7))
                out.push([d, null]);
            break;
        }
        case 'biweekly': {
            const anchor = s.referenceDate ?? version.validFrom;
            const diff = daysBetween(anchor, from);
            const k0 = Math.ceil(diff / 14);
            for (let d = addDays(anchor, k0 * 14); d <= to; d = addDays(d, 14)) {
                if (d >= from)
                    out.push([d, null]);
            }
            break;
        }
        case 'monthly': {
            const day = s.dayOfMonth ?? parts(s.referenceDate ?? version.validFrom).day;
            let { year, month } = parts(from);
            for (;;) {
                const d = clampDay(year, month, day);
                if (d > to)
                    break;
                if (d >= from)
                    out.push([d, null]);
                month += 1;
                if (month > 12) {
                    month = 1;
                    year += 1;
                }
            }
            break;
        }
        case 'bimonthly': {
            const anchor = s.referenceDate ?? version.validFrom;
            const day = s.dayOfMonth ?? parts(anchor).day;
            const anchorPeriod = periodOf(anchor);
            const diffMonths = monthsBetween(anchorPeriod, periodOf(from));
            let m = Math.max(0, diffMonths - 3);
            while (m % 2 !== 0)
                m += 1;
            for (;; m += 2) {
                const p = addMonthsToPeriod(anchorPeriod, m);
                const { year, month } = periodParts(p);
                const d = clampDay(year, month, day);
                if (d > to)
                    break;
                if (d >= from)
                    out.push([d, null]);
            }
            break;
        }
        case 'annual': {
            const ref = s.referenceDate ?? version.validFrom;
            const day = s.dayOfMonth ?? parts(ref).day;
            const month = s.monthOfYear ?? parts(ref).month;
            for (let year = parts(from).year;; year++) {
                const d = clampDay(year, month, day);
                if (d > to)
                    break;
                if (d >= from)
                    out.push([d, null]);
            }
            break;
        }
        case 'once': {
            const first = s.referenceDate ?? version.validFrom;
            const n = s.instalments ?? 1;
            const count = n && n > 0 ? n : 1;
            const day = parts(first).day;
            for (let i = 0; i < count; i++) {
                const d = i === 0 ? first : addMonths(first, i, day);
                if (d > to)
                    break;
                if (d >= from)
                    out.push([d, count > 1 ? i + 1 : null]);
            }
            break;
        }
        case 'weekend':
        case null:
            break;
        default:
            break;
    }
    return out;
}
/**
 * Gasto fijo mensual: un cargo por cada mes en que está activo, sin importar el día (confirmado
 * 22/10/2026). El día configurado es orientativo; si en ese mes cae fuera de la vigencia se usa el día
 * activo más cercano. Si dos versiones activas comparten un mes, gana la vigente el día de cobro.
 * Devuelve, por id de versión, las fechas que le corresponden.
 */
function monthlyFixedPlan(segments, range) {
    const chosen = new Map();
    for (const seg of segments) {
        const v = seg.version;
        if (v.state !== 'active' || v.frequency !== 'monthly' || v.amountCents === null)
            continue;
        const from = seg.from > range.from ? seg.from : range.from;
        const to = seg.to !== null && seg.to < range.to ? seg.to : range.to;
        if (from > to)
            continue;
        const day = v.schedule.dayOfMonth ?? parts(v.schedule.referenceDate ?? v.validFrom).day;
        for (let p = periodOf(from); p <= periodOf(to); p = addMonthsToPeriod(p, 1)) {
            const { year, month } = periodParts(p);
            const billing = clampDay(year, month, day);
            const natural = billing >= seg.from && (seg.to === null || billing <= seg.to);
            const date = natural ? billing : billing < seg.from ? seg.from : seg.to;
            if (date < from || date > to)
                continue;
            const prior = chosen.get(p);
            if (prior && (prior.natural || !natural))
                continue;
            chosen.set(p, { versionId: v.id, date, natural });
        }
    }
    const byVersion = new Map();
    for (const { versionId, date } of chosen.values()) {
        const list = byVersion.get(versionId) ?? [];
        list.push([date, null]);
        byVersion.set(versionId, list);
    }
    return byVersion;
}
/**
 * Genera ocurrencias efectivas de un concepto en un rango de fechas, aplicando versiones vigentes y
 * excepciones. Determinista: recalcular no duplica; la identidad de cada ocurrencia es su fecha programada.
 * El rango se amplía para que una ocurrencia movida hacia dentro del rango no se pierda.
 */
export function generateOccurrences(concept, versions, exceptions, range) {
    const kind = kindFor(concept);
    const occurrences = [];
    const cancelled = [];
    const financing = [];
    const byOriginal = new Map();
    for (const e of exceptions) {
        if (e.conceptId !== concept.id)
            continue;
        const list = byOriginal.get(e.originalDate) ?? [];
        list.push(e);
        byOriginal.set(e.originalDate, list);
    }
    // Rango ampliado: 2 meses a cada lado para capturar movimientos y desplazamiento de período.
    const wide = { from: addMonths(range.from, -2), to: addMonths(range.to, 2) };
    const fromPeriod = periodOf(range.from);
    const toPeriod = periodOf(range.to);
    /** Una ocurrencia pertenece al rango si su período imputado cae dentro (la fecha puede ser del mes anterior). */
    const periodInRange = (p) => p >= fromPeriod && p <= toPeriod;
    const segments = versionSegments(versions);
    const monthlyPlan = concept.behavior === 'fixed' ? monthlyFixedPlan(segments, wide) : new Map();
    for (const seg of segments) {
        const v = seg.version;
        if (v.state !== 'active')
            continue;
        if (concept.behavior === 'loan' && v.principalCents && v.principalCents > 0) {
            const date = v.schedule.referenceDate ?? v.validFrom;
            // Una entrada de financiación por fecha de recepción: las versiones posteriores (cambio de cuota)
            // copian el importe recibido y no deben contarlo otra vez. Se devuelve aunque caiga fuera del rango
            // porque el seguimiento del préstamo necesita el importe recibido.
            if (!financing.some((f) => f.date === date)) {
                financing.push({
                    key: `${concept.id}:financing:${date}`,
                    conceptId: concept.id,
                    date,
                    originalDate: date,
                    servicePeriod: periodOf(date),
                    period: periodOf(date),
                    amountCents: v.principalCents,
                    versionId: v.id,
                    kind: 'financing',
                    status: 'scheduled',
                    instalmentNumber: null,
                    instalmentTotal: null,
                });
            }
        }
        if (concept.behavior === 'budget' || v.frequency === null || v.frequency === 'weekend')
            continue;
        if (v.amountCents === null)
            continue;
        const offset = v.schedule.periodOffset ?? 0;
        const total = v.frequency === 'once' && v.schedule.instalments && v.schedule.instalments > 1 ? v.schedule.instalments : null;
        // Gasto fijo mensual: un cargo por mes activo (ver monthlyFixedPlan).
        const dates = concept.behavior === 'fixed' && v.frequency === 'monthly' ? monthlyPlan.get(v.id) ?? [] : scheduledDates(v, seg.from, seg.to, wide);
        for (const [date, instalment] of dates) {
            const key = occurrenceKey(concept.id, date);
            const exs = byOriginal.get(date) ?? [];
            const cancel = exs.find((e) => e.kind === 'cancel');
            const move = exs.find((e) => e.kind === 'move');
            if (cancel) {
                if (periodInRange(shiftPeriod(date, offset))) {
                    cancelled.push({ key, conceptId: concept.id, originalDate: date, period: shiftPeriod(date, offset), amountCents: v.amountCents, note: cancel.note });
                }
                continue;
            }
            const effective = move?.newDate ?? date;
            if (!periodInRange(shiftPeriod(effective, offset)))
                continue;
            occurrences.push({
                key,
                conceptId: concept.id,
                date: effective,
                originalDate: date,
                servicePeriod: periodOf(effective),
                period: shiftPeriod(effective, offset),
                amountCents: move?.amountCents ?? v.amountCents,
                versionId: v.id,
                kind,
                status: move ? 'moved' : 'scheduled',
                instalmentNumber: instalment,
                instalmentTotal: total,
            });
        }
    }
    for (const e of exceptions) {
        if (e.conceptId !== concept.id || e.kind !== 'add')
            continue;
        const v = versionAt(versions, e.originalDate);
        const amount = e.amountCents ?? v?.amountCents ?? 0;
        const offset = v?.schedule.periodOffset ?? 0;
        if (!periodInRange(shiftPeriod(e.originalDate, offset)))
            continue;
        occurrences.push({
            key: `${concept.id}:add:${e.id}`,
            conceptId: concept.id,
            date: e.originalDate,
            originalDate: e.originalDate,
            servicePeriod: periodOf(e.originalDate),
            period: shiftPeriod(e.originalDate, offset),
            amountCents: amount,
            versionId: v?.id ?? '',
            kind,
            status: 'added',
            instalmentNumber: null,
            instalmentTotal: null,
        });
    }
    occurrences.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.key < b.key ? -1 : 1));
    cancelled.sort((a, b) => (a.originalDate < b.originalDate ? -1 : 1));
    return { occurrences, cancelled, financing };
}
//# sourceMappingURL=schedule.js.map